import "leaflet/dist/leaflet.css";
import { useTranslation } from "react-i18next";
import { MapContainer, TileLayer, CircleMarker, Tooltip, LayerGroup, LayersControl } from "react-leaflet";
import { Globe2 } from "lucide-react";
import { NeuCard } from "../../components/neu/Neu";

const ESRI = "https://server.arcgisonline.com/ArcGIS/rest/services";
const IMAGERY = `${ESRI}/World_Imagery/MapServer/tile/{z}/{y}/{x}`;
const LABELS = `${ESRI}/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}`;
const OSM = "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png";

// قمر صناعي (Esri + تسميات إنجليزية) أو خريطة شوارع (OSM بأسماء محلية/عربية)، قابلة للتبديل + تكبير/تصغير.
export const NotifyWorldMap = ({ geo = {} }) => {
  const { t } = useTranslation();
  const points = (geo.points || []).filter((p) => p.lat != null && p.lng != null);
  const max = Math.max(1, ...points.map((p) => p.recipients));
  const center = points.length ? [points[0].lat, points[0].lng] : [24.0, 45.0];
  return (
    <NeuCard data-testid="notify-world-map">
      <div className="flex items-center justify-between gap-2">
        <p className="label-xs flex items-center gap-2"><Globe2 className="h-4 w-4 text-primary" /> {t("platform.map.title")}</p>
        <span className="rounded-full bg-primary/10 px-3 py-1 text-xs font-bold text-primary" data-testid="notify-map-count">
          {t("platform.map.count", { n: points.length })}
        </span>
      </div>
      <div className="relative mx-auto mt-3 w-full max-w-4xl overflow-hidden rounded-2xl ring-1 ring-border/50" data-testid="notify-map-canvas">
        <MapContainer center={center} zoom={points.length ? 4 : 2} minZoom={2} maxBounds={[[-85, -180], [85, 180]]}
          maxBoundsViscosity={1} scrollWheelZoom style={{ height: 460, width: "100%", background: "#0b2640" }}>
          <LayersControl position="topright">
            <LayersControl.BaseLayer checked name={t("platform.map.satellite")}>
              <LayerGroup>
                <TileLayer attribution="&copy; Esri" url={IMAGERY} noWrap />
                <TileLayer url={LABELS} noWrap />
              </LayerGroup>
            </LayersControl.BaseLayer>
            <LayersControl.BaseLayer name={t("platform.map.streets")}>
              <TileLayer attribution="&copy; OpenStreetMap" url={OSM} noWrap />
            </LayersControl.BaseLayer>
          </LayersControl>
          {points.map((p, i) => (
            <CircleMarker key={i} center={[p.lat, p.lng]} radius={7 + (p.recipients / max) * 11}
              pathOptions={{ color: "#ffffff", weight: 2, fillColor: "#f97316", fillOpacity: 0.9 }} data-testid={`geo-point-${i}`}>
              <Tooltip sticky direction="top" offset={[0, -4]}>{t("platform.map.tooltip", { label: p.label || p.city || "", n: p.recipients })}</Tooltip>
            </CircleMarker>
          ))}
        </MapContainer>
      </div>
      {points.length ? (
        <div className="mt-3 flex flex-wrap gap-2" data-testid="notify-geo-list">
          {points.slice(0, 12).map((p, i) => (
            <span key={i} data-testid={`geo-row-${i}`} className="rounded-full px-3 py-1 text-xs font-semibold shadow-neu-sm">
              {t("platform.map.row", { label: p.label || p.city, n: p.recipients })}{p.city ? ` · ${p.city}` : ""}
            </span>
          ))}
        </div>
      ) : (
        <p className="mt-3 text-sm text-muted-foreground" data-testid="notify-geo-empty">{t("platform.map.empty")}</p>
      )}
    </NeuCard>
  );
};
